import { FiClock } from 'react-icons/fi';

export const pastSimpleData = {
  courseId: 'past-simple',
  courseType: 'grammar',
  title: 'Past Simple Tense',
  icon: FiClock,
  overview: {
    impact: 'Narrating past events — vital for Speaking Part 2',
    focus: 'Regular and irregular past tense forms',
    uses: 'Speaking Parts 2-3, Writing Task 2',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Regular Past Simple',
      content: `<p>Add <strong>-ed</strong> to the base verb: work→worked, play→played.</p><p>Spelling rules: verbs ending in -e add -d (live→lived). CVC verbs double (stop→stopped). Consonant+y changes to -ied (study→studied).</p>`,
      examples: [
        {
          label: 'Regular',
          text: 'I worked in a hospital last year.'
        },
        {
          label: 'Negative',
          text: 'She didn\'t go to the party.'
        },
        {
          label: 'Question',
          text: 'Did you enjoy the film?'
        }
      ]
    },
    {
      title: 'Irregular Verbs',
      content: `<p>Many common verbs have irregular past forms that must be memorized:</p>`,
      table: {
        headers: [
          'Base',
          'Past Simple',
          'Base',
          'Past Simple'
        ],
        rows: [
          [
            'go',
            'went',
            'see',
            'saw'
          ],
          [
            'eat',
            'ate',
            'take',
            'took'
          ],
          [
            'give',
            'gave',
            'come',
            'came'
          ],
          [
            'make',
            'made',
            'write',
            'wrote'
          ],
          [
            'speak',
            'spoke',
            'think',
            'thought'
          ]
        ]
      }
    },
    {
      title: 'Past Simple vs Present Perfect',
      content: `<p>A critical distinction for IELTS:</p>`,
      points: [
        'Past simple = finished time: \'I visited London last year.\'',
        'Present perfect = unfinished time or result now: \'I have visited London three times.\'',
        'Past simple uses time markers: yesterday, last week, in 2020, ago',
        'Present perfect uses: ever, never, already, yet, since, for'
      ]
    }
  ],
  exercises: [
    {
      title: 'Regular or Irregular?',
      type: 'select',
      instructions: 'Choose the correct past simple form.',
      questions: [
        {
          text: 'I ___ (go) to the cinema yesterday.',
          options: [
            'went',
            'goed',
            'gone'
          ],
          answer: 'went',
          explanation: '\'Go\' is irregular: go→went.'
        },
        {
          text: 'She ___ (study) French at school.',
          options: [
            'studied',
            'studyed',
            'studed'
          ],
          answer: 'studied',
          explanation: 'Consonant+y → change to -ied.'
        },
        {
          text: 'They ___ (eat) dinner at 7 pm.',
          options: [
            'ate',
            'eated',
            'eaten'
          ],
          answer: 'ate',
          explanation: '\'Eat\' is irregular: eat→ate.'
        },
        {
          text: 'We ___ (play) tennis last weekend.',
          options: [
            'played',
            'plaied',
            'plaid'
          ],
          answer: 'played',
          explanation: 'Regular: play + -ed = played.'
        }
      ]
    }
  ],
  tips: [
    'Learn the top 50 irregular verbs by heart',
    'Past simple ALWAYS uses \'did\' for questions/negatives (not \'was\')',
    'Time markers: yesterday, ago, last week, in 2019',
    'Don\'t confuse with present perfect — past simple = finished time'
  ],
  dailyPractice: [
    'Practice past simple tense concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Past Simple: Regular (-ed) and irregular forms for completed past actions with finished time references. Key for IELTS Speaking Part 2 narratives.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...pastSimpleData,
      title: pastSimpleData.title + ' (Foundations)',
      overview: { ...pastSimpleData.overview, duration: '15-20 minutes' },
      sections: pastSimpleData.sections.slice(0, Math.max(2, Math.ceil(pastSimpleData.sections.length / 2))),
      exercises: pastSimpleData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...pastSimpleData,
      title: pastSimpleData.title + ' (Advanced)',
      overview: { ...pastSimpleData.overview, duration: '30-35 minutes' },
      sections: pastSimpleData.sections,
      exercises: pastSimpleData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(pastSimpleData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return pastSimpleData;
}
